import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { Button, List, Popup, Switch, Tag, TextArea, Toast } from 'antd-mobile';
import {
  EnvironmentOutline,
  TeamOutline,
  FileOutline,
  CheckOutline,
  AddOutline,
  ClockCircleOutline,
} from 'antd-mobile-icons';
import dayjs from 'dayjs';
import { mobileApi } from '../api';
import { T, SectionCard, TextInput } from '../theme';

// Отметки старшего группы на странице задания «Тест» (Госзадание GO):
// транспорт, состав группы, маршрут с геолокацией, чек-лист инспектора.
// Отметки пишутся в связанную карточку наряда (dispatch_card_id).
// Старшему группы — интерактив, остальным исполнителям — read-only.

function formatTime(iso) {
  if (!iso) return null;
  return dayjs(iso).tz('Europe/Moscow').format('HH:mm');
}

function formatCoords(lat, lon) {
  if (lat === null || lat === undefined) return '';
  return ` (${Number(lat).toFixed(5)}, ${Number(lon).toFixed(5)})`;
}

function getPosition() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Геолокация не поддерживается'));
      return;
    }

    const tryGetPosition = (highAccuracy) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
        (err) => {
          if (highAccuracy) {
            tryGetPosition(false);
          } else {
            let msg = 'Не удалось определить геолокацию';
            if (err.code === 1) {
              msg = 'Доступ к геолокации запрещён. Проверьте разрешение в настройках браузера/устройства.';
            } else if (err.code === 2) {
              msg = 'Геолокация недоступна. Включите GPS/местоположение и попробуйте на открытом пространстве.';
            } else if (err.code === 3) {
              msg = 'Превышено время ожидания геолокации. Попробуйте ещё раз на улице или с включённым GPS.';
            }
            reject(new Error(msg));
          }
        },
        {
          enableHighAccuracy: highAccuracy,
          timeout: highAccuracy ? 10000 : 15000,
          maximumAge: highAccuracy ? 0 : 60000,
        }
      );
    };

    tryGetPosition(true);
  });
}

const ROUTE_STEPS = [
  { key: 'depart-office', label: 'Убыл из офиса', at: 'departed_office_at', lat: 'departed_office_lat', lon: 'departed_office_lon' },
  { key: 'arrive-object', label: 'Прибыл на объект', at: 'arrived_at_object_at', lat: 'arrived_at_object_lat', lon: 'arrived_at_object_lon' },
  { key: 'depart-object', label: 'Убыл с объекта', at: 'departed_object_at', lat: 'departed_object_lat', lon: 'departed_object_lon' },
  { key: 'arrive-office', label: 'Прибыл в офис', at: 'arrived_office_at', lat: 'arrived_office_lat', lon: 'arrived_office_lon' },
];

function MemberEditPopup({ visible, title, initialName, onClose, onSubmit, submitText }) {
  const [name, setName] = useState(initialName || '');
  useEffect(() => {
    if (visible) setName(initialName || '');
  }, [visible, initialName]);
  return (
    <Popup visible={visible} onMaskClick={onClose} bodyStyle={{ borderRadius: '12px 12px 0 0' }}>
      <div
        style={{
          padding: '12px 16px',
          borderBottom: '1px solid #eee',
          fontWeight: 600,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <span>{title}</span>
        <span onClick={onClose} style={{ color: T.primary, fontSize: 14, fontWeight: 400 }}>
          Закрыть
        </span>
      </div>
      <div style={{ padding: '12px 16px 16px' }}>
        <TextInput placeholder="ФИО сотрудника" value={name} onChange={setName} clearable />
        <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
          <Button block onClick={onClose}>
            Отмена
          </Button>
          <Button block color="primary" onClick={() => name.trim() && onSubmit(name.trim())}>
            {submitText}
          </Button>
        </div>
      </div>
    </Popup>
  );
}

function TestTaskDispatch({ taskId }) {
  const [hidden, setHidden] = useState(false);
  const [card, setCard] = useState(null);
  const [isSenior, setIsSenior] = useState(false);
  const [seniorMembers, setSeniorMembers] = useState([]);
  const [editableMembers, setEditableMembers] = useState([]);
  const [checklist, setChecklist] = useState({});
  const [geoLoading, setGeoLoading] = useState(null);
  const [membersSaving, setMembersSaving] = useState(false);
  const [checklistSaving, setChecklistSaving] = useState(false);
  const [replaceIdx, setReplaceIdx] = useState(null);
  const [addVisible, setAddVisible] = useState(false);

  const { data } = useQuery(
    ['goszadanieGoDispatch', taskId],
    () => mobileApi.goszadanieGoDispatch(taskId),
    {
      retry: false,
      onError: (err) => {
        // 404 — задание не связано с карточкой наряда: блоки скрываем
        if (err?.response?.status === 404) {
          setHidden(true);
        } else {
          const detail = err?.response?.data?.detail || 'Не удалось загрузить отметки старшего';
          Toast.show({ icon: 'fail', content: String(detail) });
        }
      },
    }
  );

  useEffect(() => {
    const payload = data?.data;
    if (!payload) return;
    const c = payload.card;
    setCard(c);
    setIsSenior(!!payload.is_senior);
    setSeniorMembers(payload.senior_members || []);
    setEditableMembers(c.confirmed_members?.length ? c.confirmed_members : c.members || []);
    setChecklist({
      inspector_arrival_time: c.checklist_inspector_arrival_time || '',
      inspector_departure_time: c.checklist_inspector_departure_time || '',
      inspector_arrived_before_group: !!c.checklist_inspector_arrived_before_group,
      inspector_left_after_group: !!c.checklist_inspector_left_after_group,
      risks_occurred: !!c.checklist_risks_occurred,
      risks_comment: c.checklist_risks_comment || '',
      notes: c.checklist_notes || '',
    });
  }, [data]);

  if (hidden || !card) return null;

  const applyCard = (c) => {
    setCard(c);
    setEditableMembers(c.confirmed_members?.length ? c.confirmed_members : c.members || []);
  };

  const failText = (err, fallback) =>
    String(err?.response?.data?.detail || err?.message || fallback);

  const handleGeo = async (step) => {
    if (geoLoading) return;
    setGeoLoading(step.key);
    try {
      const geo = await getPosition();
      const res = await mobileApi.goszadanieGoDispatchGeo(taskId, step.key, {
        gps_lat: geo.lat,
        gps_lon: geo.lon,
      });
      applyCard(res.data);
      Toast.show({ icon: 'success', content: `${step.label} — зафиксировано` });
    } catch (err) {
      Toast.show({ icon: 'fail', content: failText(err, 'Не удалось определить геолокацию') });
    } finally {
      setGeoLoading(null);
    }
  };

  const handleConfirmMembers = async (membersOverride) => {
    if (membersSaving) return;
    setMembersSaving(true);
    try {
      const res = await mobileApi.goszadanieGoDispatchConfirm(taskId, membersOverride || editableMembers);
      applyCard(res.data);
      Toast.show({ icon: 'success', content: 'Состав сохранён' });
    } catch (err) {
      Toast.show({ icon: 'fail', content: failText(err, 'Ошибка сохранения состава') });
    } finally {
      setMembersSaving(false);
    }
  };

  const handleRemoveMember = (idx) => {
    handleConfirmMembers(editableMembers.filter((_, i) => i !== idx));
  };

  const handleReplaceMember = (name) => {
    const next = editableMembers.map((m, i) =>
      i === replaceIdx ? { ...m, id: null, full_name: name } : m
    );
    setReplaceIdx(null);
    handleConfirmMembers(next);
  };

  const handleAddMember = (name) => {
    setAddVisible(false);
    handleConfirmMembers([...editableMembers, { id: null, full_name: name }]);
  };

  const handleSaveChecklist = async () => {
    if (checklistSaving) return;
    setChecklistSaving(true);
    try {
      const res = await mobileApi.goszadanieGoDispatchChecklist(taskId, checklist);
      applyCard(res.data);
      Toast.show({ icon: 'success', content: 'Чек-лист сохранён' });
    } catch (err) {
      Toast.show({ icon: 'fail', content: failText(err, 'Ошибка сохранения чек-листа') });
    } finally {
      setChecklistSaving(false);
    }
  };

  const currentStepIdx = ROUTE_STEPS.findIndex((s) => !card[s.at]);
  const hasChecklistData =
    card.checklist_inspector_arrival_time ||
    card.checklist_inspector_departure_time ||
    card.checklist_inspector_arrived_before_group ||
    card.checklist_inspector_left_after_group ||
    card.checklist_risks_occurred ||
    card.checklist_notes;
  const displayMembers = card.confirmed_members?.length ? card.confirmed_members : card.members || [];

  return (
    <>
      <SectionCard title="Транспорт" icon={<EnvironmentOutline />}>
        <div style={{ fontSize: 14, color: T.text }}>
          {card.vehicle?.model || 'Авто не назначено'}
          {card.vehicle?.license_plate && ` · ${card.vehicle.license_plate}`}
        </div>
        <div style={{ marginTop: 6, fontSize: 13, color: T.label, display: 'flex', alignItems: 'center', gap: 5 }}>
          <ClockCircleOutline style={{ fontSize: 13 }} />
          Выезд: {card.departure_time || '—'} · Возврат: {card.return_time || '—'}
        </div>
      </SectionCard>

      <SectionCard
        title="Состав группы"
        icon={<TeamOutline />}
        extra={
          card.confirmed_at ? (
            <Tag color="success">Подтверждён</Tag>
          ) : (
            <Tag color="warning">Не подтверждён</Tag>
          )
        }
      >
        {seniorMembers.length > 0 && (
          <div style={{ fontSize: 12, color: T.hint, marginBottom: 8 }}>
            Старший: {seniorMembers.join(', ')}
          </div>
        )}
        {isSenior ? (
          <>
            <List style={{ '--border-top': 'none', '--border-bottom': 'none' }}>
              {editableMembers.map((m, idx) => (
                <List.Item
                  key={idx}
                  extra={
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Button size="mini" onClick={() => setReplaceIdx(idx)}>
                        Заменить
                      </Button>
                      <Button size="mini" color="danger" onClick={() => handleRemoveMember(idx)}>
                        Удалить
                      </Button>
                    </div>
                  }
                >
                  {m.full_name}
                </List.Item>
              ))}
            </List>
            <Button block size="small" style={{ marginTop: 8 }} onClick={() => setAddVisible(true)}>
              <AddOutline style={{ marginRight: 4 }} />
              Добавить сотрудника
            </Button>
            {!card.departed_office_at && (
              <Button
                block
                color="primary"
                style={{ marginTop: 10 }}
                loading={membersSaving}
                onClick={() => handleConfirmMembers()}
              >
                {card.confirmed_at ? 'Сохранить состав' : 'Подтвердить состав'}
              </Button>
            )}
          </>
        ) : (
          <div>
            {displayMembers.map((m, idx) => (
              <div
                key={idx}
                style={{
                  padding: '8px 0',
                  borderBottom: idx < displayMembers.length - 1 ? `1px solid ${T.divider}` : 'none',
                  fontSize: 14,
                  color: T.text,
                }}
              >
                {m.full_name}
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      <SectionCard title="Маршрут на объекте" icon={<EnvironmentOutline />}>
        {isSenior ? (
          <>
            {ROUTE_STEPS.map((step, idx) => (
              <div key={step.key} style={{ marginBottom: idx < ROUTE_STEPS.length - 1 ? 10 : 0 }}>
                <Button
                  block
                  color={card[step.at] ? 'default' : 'primary'}
                  disabled={card[step.at] || idx !== currentStepIdx || (idx === 0 && !card.confirmed_at)}
                  loading={geoLoading === step.key}
                  onClick={() => handleGeo(step)}
                >
                  {card[step.at] ? <CheckOutline style={{ marginRight: 4 }} /> : null}
                  {step.label}
                </Button>
                {card[step.at] && (
                  <div style={{ marginTop: 4, fontSize: 12, color: T.label }}>
                    {step.label} в {formatTime(card[step.at])}
                    {formatCoords(card[step.lat], card[step.lon])}
                  </div>
                )}
              </div>
            ))}
            {!card.confirmed_at && (
              <div style={{ fontSize: 12, color: T.danger, marginTop: 6 }}>
                Подтвердите состав группы, чтобы фиксировать маршрут
              </div>
            )}
          </>
        ) : (
          <>
            {ROUTE_STEPS.every((s) => !card[s.at]) ? (
              <div style={{ fontSize: 13, color: T.hint }}>Отметок маршрута пока нет</div>
            ) : (
              ROUTE_STEPS.filter((s) => card[s.at]).map((s) => (
                <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', fontSize: 13, color: T.text }}>
                  <CheckOutline style={{ color: T.success, flexShrink: 0 }} />
                  <span>
                    {s.label} в {formatTime(card[s.at])}
                    <span style={{ color: T.hint }}>{formatCoords(card[s.lat], card[s.lon])}</span>
                  </span>
                </div>
              ))
            )}
          </>
        )}
      </SectionCard>

      {(isSenior || hasChecklistData) && (
        <SectionCard title="Чек-лист инспектора" icon={<FileOutline />}>
          {isSenior ? (
            <>
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 13, color: T.label, marginBottom: 6 }}>Время прибытия инспектора</div>
                <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontSize: 14 }}>Прибыл раньше группы экспертов</span>
                  <Switch
                    checked={checklist.inspector_arrived_before_group}
                    onChange={(v) =>
                      setChecklist((p) => ({
                        ...p,
                        inspector_arrived_before_group: v,
                        inspector_arrival_time: v ? '' : p.inspector_arrival_time,
                      }))
                    }
                  />
                </div>
                {!checklist.inspector_arrived_before_group && (
                  <TextInput
                    value={checklist.inspector_arrival_time}
                    onChange={(v) => setChecklist((p) => ({ ...p, inspector_arrival_time: v }))}
                    placeholder="ЧЧ:ММ"
                  />
                )}
              </div>
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 13, color: T.label, marginBottom: 6 }}>Время убытия инспектора</div>
                <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontSize: 14 }}>Убыл позже группы экспертов</span>
                  <Switch
                    checked={checklist.inspector_left_after_group}
                    onChange={(v) =>
                      setChecklist((p) => ({
                        ...p,
                        inspector_left_after_group: v,
                        inspector_departure_time: v ? '' : p.inspector_departure_time,
                      }))
                    }
                  />
                </div>
                {!checklist.inspector_left_after_group && (
                  <TextInput
                    value={checklist.inspector_departure_time}
                    onChange={(v) => setChecklist((p) => ({ ...p, inspector_departure_time: v }))}
                    placeholder="ЧЧ:ММ"
                  />
                )}
              </div>
              <div style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 14 }}>Возникли риски</span>
                <Switch
                  checked={checklist.risks_occurred}
                  onChange={(v) => setChecklist((p) => ({ ...p, risks_occurred: v }))}
                />
              </div>
              {checklist.risks_occurred && (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 13, color: T.label, marginBottom: 6 }}>Комментарий по рискам</div>
                  <TextArea
                    rows={2}
                    value={checklist.risks_comment}
                    onChange={(v) => setChecklist((p) => ({ ...p, risks_comment: v }))}
                    placeholder="Опишите риски"
                    style={{ background: T.subBlockBg, borderRadius: 8, padding: '9px 12px', '--font-size': '15px' }}
                  />
                </div>
              )}
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 13, color: T.label, marginBottom: 6 }}>Примечания</div>
                <TextArea
                  rows={2}
                  value={checklist.notes}
                  onChange={(v) => setChecklist((p) => ({ ...p, notes: v }))}
                  placeholder="Примечания"
                  style={{ background: T.subBlockBg, borderRadius: 8, padding: '9px 12px', '--font-size': '15px' }}
                />
              </div>
              <Button block color="primary" loading={checklistSaving} onClick={handleSaveChecklist}>
                Сохранить чек-лист
              </Button>
            </>
          ) : (
            <div style={{ fontSize: 13, color: T.text, lineHeight: 1.7 }}>
              <div>
                Прибытие инспектора:{' '}
                {card.checklist_inspector_arrived_before_group
                  ? 'раньше группы экспертов'
                  : card.checklist_inspector_arrival_time || '—'}
              </div>
              <div>
                Убытие инспектора:{' '}
                {card.checklist_inspector_left_after_group
                  ? 'позже группы экспертов'
                  : card.checklist_inspector_departure_time || '—'}
              </div>
              {card.checklist_risks_occurred && (
                <div style={{ color: T.danger }}>
                  Возникли риски{card.checklist_risks_comment ? `: ${card.checklist_risks_comment}` : ''}
                </div>
              )}
              {card.checklist_notes && <div>Примечания: {card.checklist_notes}</div>}
            </div>
          )}
        </SectionCard>
      )}

      <MemberEditPopup
        visible={replaceIdx !== null}
        title="Заменить сотрудника"
        initialName={replaceIdx !== null ? editableMembers[replaceIdx]?.full_name : ''}
        onClose={() => setReplaceIdx(null)}
        onSubmit={handleReplaceMember}
        submitText="Сохранить"
      />
      <MemberEditPopup
        visible={addVisible}
        title="Добавить сотрудника"
        initialName=""
        onClose={() => setAddVisible(false)}
        onSubmit={handleAddMember}
        submitText="Добавить"
      />
    </>
  );
}

export default TestTaskDispatch;
